




































































































































import Vue from 'vue'
import { CLASSIFIED_VIEW_NS } from '~/store/modules/shared/classifieds/view/state'
import CFormError from '~/components/shared/configurable/form/CFormError.vue'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'
import CForm from '~/components/shared/configurable/form/CForm.vue'
import CFormGroup from '~/components/shared/configurable/form/CFormGroup.vue'
import { useModalToggle } from '~/compositions/modal-toggle'
import {
  computed,
  nextTick,
  onMounted,
  PropType,
  ref,
  toRefs
} from '@nuxtjs/composition-api'
import { useNamespacedStore } from '~/compositions/store'
import { toCamelCase } from '~/utils/object'
import { useLogger } from '~/compositions/logger'
import { useSnackbar } from '~/compositions/snackbar'
import ClassifiedOffersService from '~/services/classified/ClassifiedOffersService'
import { useDep } from '~/compositions/dependency-container'
import { useI18n } from '~/compositions/i18n'
import { SET_HAS_OFFERS_FOR_CLASSIFIED } from '~/store/modules/shared/classifieds/view/mutation-types'
import { ClassifiedOfferEntry, OfferState } from '~/models/classified/offers'
import CPrice from '~/components/shared/configurable/CPrice.vue'
import { ciOffers } from '~/icons/source/regular/offers'
import CTransitionExpand from '~/components/shared/configurable/expand/CTransitionExpand.vue'
import { ciPlus } from '~/icons/source/light/plus'
import OfferAmountInput from '~/components/shared/classified/offers/OfferAmountInput.vue'
import { DynamicFormSchema } from '~/models/dynamic-form/schema'
import CInfoTooltip from '~/components/shared/configurable/info/CInfoTooltip.vue'

export default Vue.extend({
  components: {
    CInfoTooltip,
    CPrice,
    OfferAmountInput,
    CFormError,
    CForm,
    CFormGroup,
    CTransitionExpand
  },
  props: {
    offerToCounter: {
      type: Object as PropType<ClassifiedOfferEntry | null>,
      required: false,
      default: null
    },
    useModalToggle: {
      type: Boolean,
      required: false,
      default: true
    },
    previousOfferExists: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  setup(props, { emit }) {
    const { offerToCounter } = toRefs(props)
    const modalToggleFunc = useModalToggle()
    const { state: viewState, commit, dispatch } = useNamespacedStore(
      CLASSIFIED_VIEW_NS
    )

    const isCounteroffer = computed(() => {
      return Boolean(
        offerToCounter.value &&
          offerToCounter.value.latestBid.state !== OfferState.WITHDRAWN
      )
    })
    const startingPrice = computed(() => {
      if (offerToCounter.value) {
        return (
          offerToCounter.value.classified.priceParked ||
          offerToCounter.value.classified.rawPrice ||
          0
        )
      }
      return (
        viewState.classified?.price.extra.price_parked ||
        viewState.classified?.price.extra.raw_price ||
        0
      )
    })
    const form = ref({
      amount: startingPrice.value
    })
    const schema = ref<DynamicFormSchema>([])
    const errors = ref({})
    const loading = ref(false)
    const showMessage = ref(false)
    const logger = useLogger()
    const snackbar = useSnackbar()
    const userStore = useNamespacedStore<UserState>(USER_NS)

    const { getters: userGetters } = userStore
    const isAdmin = userGetters('isAdmin')
    const classifiedOffersService = useDep(ClassifiedOffersService)
    const submitbutton = ref(null)
    const classifiedViewStore = useNamespacedStore<UserState>(
      CLASSIFIED_VIEW_NS
    )
    const { getters: classifiedViewGetters } = classifiedViewStore
    const userIsAdminOrOwner = computed(() => {
      if (offerToCounter.value) {
        return (
          userGetters('userId') ===
            offerToCounter.value?.classified.seller.id ||
          userGetters('userId') ===
            offerToCounter.value?.classified.seller.managerId ||
          isAdmin
        )
      }
      return classifiedViewGetters('userIsAdminOrOwner')
    })
    const { t } = useI18n()
    const messageRef = ref(null)

    const hideModal = () => {
      emit('close')
    }

    const triggerSubmit = e => {
      e.preventDefault()
      submitbutton.value.click()
    }

    const fetchSchema = async () => {
      try {
        loading.value = true
        let result: DynamicFormSchema

        if (offerToCounter.value) {
          result = await classifiedOffersService.getNewBidSchema(
            offerToCounter.value?.id
          )
        } else {
          result = await classifiedOffersService.getNewOfferSchema(
            viewState.classified?.id
          )
        }
        schema.value = result.schema
      } catch (error) {
        if (
          error.response?.data?.data?.errors ||
          error.response?.data?.data?.error
        ) {
          errors.value = toCamelCase(
            error.response?.data?.data?.errors ||
              error.response?.data?.data?.error
          )
        } else {
          snackbar.error(t('an error occurred please try again later'))
        }
        logger.captureError(error)
      } finally {
        loading.value = false
      }
    }

    const onSubmit = async () => {
      errors.value = {}
      try {
        loading.value = true
        let updatedOffer = null
        if (offerToCounter.value) {
          const { offer } = await classifiedOffersService.submitNewCounteroffer(
            offerToCounter.value.id,
            form.value
          )
          updatedOffer = offer
          snackbar.success(t('counteroffer sent successfully'))
        } else {
          const { offer } = await classifiedOffersService.submitNewOffer(
            viewState.classified?.id,
            form.value
          )
          updatedOffer = offer
          snackbar.success(t('offer sent successfully'))
          commit(SET_HAS_OFFERS_FOR_CLASSIFIED, true)
        }
        emit('update-offer', updatedOffer)
        await dispatch(`fetchOfferCount`, !userIsAdminOrOwner.value)
        hideModal()
      } catch (error) {
        if (error.response?.data?.data?.errors) {
          errors.value = toCamelCase(error.response.data.data.errors)
        } else {
          snackbar.error(t('an error occurred please try again later'))
        }
        logger.captureError(error)
      } finally {
        loading.value = false
      }
    }

    const getDynamicSchemaEntry = (name: string) => {
      if (schema.value) {
        return schema.value.find(s => s.name === name)
      }
      return null
    }

    const minPercentage = computed(() => {
      const amountSchema = getDynamicSchemaEntry('amount')
      if (amountSchema) {
        return amountSchema.pricePercentage
      }
      return null
    })

    const explainText = computed(() => {
      if (userIsAdminOrOwner.value) {
        return t(
          'after receiving your counteroffer, the interested buyer can accept it, reject it or make another counteroffer'
        )
      } else if (isCounteroffer.value) {
        return t(
          'after receiving your counteroffer, the seller can accept it, reject it or make a counteroffer'
        )
      }
      return t(
        'after receiving your offer, the seller can accept it, reject it or make a counteroffer'
      )
    })

    onMounted(() => {
      modalToggleFunc.setModalInSearch(false)
      fetchSchema()
    })

    const addMessageClick = async () => {
      showMessage.value = true
      await nextTick()
      messageRef.value.focus()
    }
    return {
      modalToggleFunc,
      isAdmin,
      form,
      loading,
      errors,
      onSubmit,
      triggerSubmit,
      submitbutton,
      hideModal,
      fetchSchema,
      schema,
      startingPrice,
      ciOffers,
      ciPlus,
      showMessage,
      getDynamicSchemaEntry,
      minPercentage,
      explainText,
      userIsAdminOrOwner,
      addMessageClick,
      messageRef,
      isCounteroffer
    }
  }
})
